import JSZip from 'jszip';
import dicomParser from 'dicom-parser';
import { TAG_DICT } from '../Scripts/dataDictionary.js';

/**
 * An array of DICOM header definitions, each containing:
 * - tag: The DICOM tag as a string (group and element, e.g., '00100010').
 * - name: The human-readable name of the DICOM attribute.
 * - anonymize: Boolean indicating if the attribute should be anonymized.
 *
 * @type {Array<{tag: string, name: string, anonymize: boolean}>}
 */
const dicomHeaders = [
    { tag: '00100010', name: "Patient's Name", anonymize: true },
    { tag: '00100020', name: 'Patient ID', anonymize: true },
    { tag: '00100030', name: "Patient's Birth Date", anonymize: true },
    { tag: '00100040', name: "Patient's Sex", anonymize: true },
    { tag: '00101000', name: 'Other Patient IDs', anonymize: true },
    { tag: '00101001', name: 'Other Patient Names', anonymize: true },
    { tag: '00102160', name: 'Ethnic Group', anonymize: true },
    { tag: '001021B0', name: 'Additional Patient History', anonymize: true },
    { tag: '00080090', name: "Referring Physician's Name", anonymize: true },
    { tag: '00080050', name: 'Accession Number', anonymize: true },
    { tag: '00081040', name: 'Institutional Department Name', anonymize: true },
    { tag: '00081048', name: "Physician(s) of Record", anonymize: true },
    { tag: '00081050', name: "Performing Physician's Name", anonymize: true },
    { tag: '00081060', name: 'Name of Physician(s) Reading Study', anonymize: true },
    { tag: '00081070', name: "Operators' Name", anonymize: true },
    { tag: '00080080', name: 'Institution Name', anonymize: true },
    { tag: '00080081', name: 'Institution Address', anonymize: true },
    { tag: '00081010', name: 'Station Name', anonymize: true },
    { tag: '00181000', name: 'Device Serial Number', anonymize: true },
    { tag: '00400275', name: 'Request Attributes Sequence', anonymize: true },
    { tag: '0040A124', name: 'UID of Person Observing', anonymize: true },
    { tag: '0040A730', name: 'Content Sequence', anonymize: true },
    { tag: '0020000D', name: 'Study Instance UID', anonymize: false },
    { tag: '0020000E', name: 'Series Instance UID', anonymize: false },
    { tag: '00200010', name: 'Study ID', anonymize: false },
    { tag: '00200011', name: 'Series Number', anonymize: false },
    { tag: '00200013', name: 'Instance Number', anonymize: false },
    { tag: '00081030', name: 'Study Description', anonymize: false },
    { tag: '0008103E', name: 'Series Description', anonymize: false },
    { tag: '00080060', name: 'Modality', anonymize: false },
    { tag: '00180015', name: 'Body Part Examined', anonymize: false },
    { tag: '00185100', name: 'Patient Position', anonymize: false },
    { tag: '00080020', name: 'Study Date', anonymize: false },
    { tag: '00080030', name: 'Study Time', anonymize: false },
    { tag: '00080021', name: 'Series Date', anonymize: false },
    { tag: '00080031', name: 'Series Time', anonymize: false },
    { tag: '00200032', name: 'Image Position (Patient)', anonymize: false },
    { tag: '00200037', name: 'Image Orientation (Patient)', anonymize: false },
    { tag: '00200052', name: 'Frame of Reference UID', anonymize: false },
    { tag: '00080018', name: 'SOP Instance UID', animator: false },
    { tag: '00082112', name: 'Source Image Sequence', anonymize: false },
];

/** * Get tag name from the DICOM tag dictionary.
 * @param {string} tag - The DICOM tag in the format '(gggg,eeee)'.
 * @returns {Object|null} The tag attributes or null if not found.
 */
function getTag(tag) {
    var group = tag.substring(1, 5);
    var element = tag.substring(5, 9);
    var tagIndex = '(' + group + ',' + element + ')'.toUpperCase();
    var attr = TAG_DICT[tagIndex];
    return attr;
}

/**
 * Extract DICOM data from a file.
 * @param {Uint8Array} byteArray - The file data as a byte array.
 * @returns {Object|null} The extracted DICOM data or null if parsing fails.
 */
function getDicomData(byteArray) {
    try {
        const dataSet = dicomParser.parseDicom(byteArray);
        const dicomHeaders = [
            { tag: '00100010', name: "Patient's Name" },
            { tag: '00100020', name: 'Patient ID' },
            { tag: '0020000E', name: 'Series Instance UID' },
            { tag: '0020000D', name: 'Study Instance UID' },
            { tag: '00080060', name: 'Modality' },
            { tag: '00081030', name: 'Study Description' },
            { tag: '00080018', name: 'SOP Instance UID' },
        ];

        return dicomHeaders.reduce((acc, { tag, name }) => {
            acc[name.replace(/\s+/g, '')] = dataSet.string(`x${tag.toLowerCase()}`);
            return acc;
        }, {
            'ReferencedSeriesInstanceUID': dataSet.elements['x00081115']?.items[0]?.dataSet?.string('x0020000e') || null,
            'InstanceNumber': dataSet.string('x00200013')?.padStart(3, '0') || null,
        });
    } catch (error) {
        console.error('Error parsing DICOM data:', error);
        return null;
    }
}

/**
 * Get the destination file name based on DICOM data and a pattern.
 * @param {Object} dicomData - The DICOM data object.
 * @param {string} [pattern='{SeriesInstanceUID}/{SOPInstanceUID}.dcm'] - The pattern for the file name.
 * @returns {string} The formatted destination file name.
 */
function getDestinationFileName(dicomData, pattern = '{SeriesInstanceUID}/{InstanceNumber}-{SOPInstanceUID}.dcm') {
    return pattern.replace(/{([^}]+)}/g, (match, p1) => {
        const value = dicomData[p1];
        if (value === undefined) {
            return match;
        }
        return value;
    });
}

/**
 * Process a DataTransferItemList from a drag-and-drop operation.
 * @param {DataTransferItemList} items - The items from the drag-and-drop event.
 * @param {Function} onProgress - Callback for progress updates.
 * @param {Function} onItemProcessed - Callback for each processed item.
 */
export function processDataTransferItemList(items, onProgress, onItemProcessed) {

    // keep track of the total number of files
    //  which may be updated when zip files are unzipped or dropped directories flattened and counted
    let numberOfFiles = items.length;
    let numberOfFilesProcessed = 0;

    // Ensure items is iterable and properly processed
    const itemArray = Array.from(items);

    console.log('Received items:', itemArray);

    // Process each item
    for (const item of itemArray) {
        console.log("Processing item:", item);
        const entry = item.webkitGetAsEntry();
        if (entry) {
            console.log('GOOD Entry:', entry);
            processFileSystemEntry(entry, (n) => { // NOTE: if we make this async, somehow all following items are invalid
                numberOfFiles += n;
                onProgress(numberOfFilesProcessed, numberOfFiles);
            }, (n) => {
                numberOfFilesProcessed += n;
                onProgress(numberOfFilesProcessed, numberOfFiles);
            }, (f) => {
                console.log('Processed file:', f);
                onItemProcessed(f);
            });
        } else {
            console.error("Invalid item:", item);
        }
        console.log('Finished processing item');
    }
}

/** 
 * process a file system entry (file or directory).
 * @param {FileSystemEntry} entry - The file system entry to process.
 * @param {Function} onCounted - Callback for counting files.
 * @param {Function} onProgress - Callback for progress updates.
 * @param {Function} onItemProcessed - Callback for each processed item.
 */
async function processFileSystemEntry(entry, onCounted, onProgress, onItemProcessed) {

    if (!entry) {
        console.error('Item is not a file or directory:', item);
        return;
    }

    if (entry.isFile) {
        const file = await new Promise((resolve, reject) => {
            entry.file(resolve, reject);
        });

        // handle zip files
        if (file.name.endsWith('.zip')) {
            console.log('Processing ZIP file:', file.name);
            await processZipFile(file, onCounted, onProgress, onItemProcessed);
        } else if (file.name.endsWith('.dcm')) {
            console.log('Processing DICOM file:', file.name);
            const fileData = await file.arrayBuffer();
            const dicomData = getDicomData(new Uint8Array(fileData));
            if (!dicomData) {
                console.warn('Ignoring file with no DICOM data:', file.name);
                return;
            }
            onItemProcessed({
                fileName: file.name,
                dicomData,
                dest: getDestinationFileName(dicomData),
                fileData: new Uint8Array(fileData),
            });
        } else {
            console.error('Unsupported file type:', file.name);
        }

        onProgress(1);
    } else if (entry.isDirectory) {
        const reader = entry.createReader();
        function readEntries() {
            return new Promise((resolve, reject) => {
                reader.readEntries(resolve, reject);
            });
        }
        let entries = [];
        let readResult;
        do {
            readResult = await readEntries();
            entries = entries.concat(readResult);
        } while (readResult.length > 0);
        onCounted(entries.length);
        onProgress(1)
        for (const e of entries) {
            await processFileSystemEntry(e, onCounted, onProgress, onItemProcessed);
        }
    }

    // console.log('Processed item:', entry);
}

/**
 * Process a single ZIP file.
 * @param {File} file - The file to process.
 * @param {Function} onProgress - Callback for progress updates.
 * @param {Function} onComplete - Callback when processing is complete.
 */
export async function processZipFile(file, onUnzip, onProgress, onFileProcessed) {
    console.log('Processing file:', file);

    if (file.size > 1 * 1024 * 1024 * 1024) {
        console.error('File is too large');
        return;
    }

    const zip = new JSZip();
    const zipData = await zip.loadAsync(file);
    console.log('Unzipped files:', Object.keys(zipData.files));

    const total = Object.keys(zipData.files).length;

    // call onUnzip and report number of files
    onUnzip(total);

    for (const fileName in zipData.files) {
        onProgress(1);

        if (!fileName.endsWith('.dcm')) {
            console.warn('Ignoring non-DICOM file:', fileName);
            continue;
        }

        const fileData = await zipData.files[fileName].async('uint8array');
        const dicomData = getDicomData(fileData);

        if (!dicomData) {
            console.warn('Ignoring file with no DICOM data:', fileName);
            continue;
        }

        onFileProcessed({
            fileName,
            dicomData,
            dest: getDestinationFileName(dicomData),
            fileData,
        });
    }
}